/**
 * Capability keys the server puts in the session payload -- and, since f9.2,
 * on each hierarchy row.
 *
 * These are NOT role codes and must never become them. A role code in the
 * browser is an authorization rule the browser invented; a capability key is
 * an answer the server already gave, derived there from the same tuple its
 * write routes enforce. When f10.1 grants Finance the right to administer the
 * hierarchy, this file does not change -- the server starts sending the key.
 *
 * Presentation only. Every endpoint refuses on its own authority and records
 * the refusal, so a hidden control is never the security boundary.
 */

/**
 * SESSION-level: may this caller write ANY hierarchy node at all (f9.1)? It
 * decides whether a list-level `New …` control is rendered. Which rows get a
 * control is the row's own `permissions`, below.
 */
export const MANAGE_HIERARCHY = 'hierarchy:manage';

/**
 * SESSION-level: may this caller create a Business Unit Group? Groups have no
 * parent row to carry `CREATE_CHILD`, and a DEM or CEM holds `MANAGE_HIERARCHY`
 * yet is refused a group create (Q-f9.2-5), so `New group` reads this key.
 */
export const CREATE_GROUP = 'hierarchy:create_group';

/**
 * SESSION-level (f16.2): may this caller start a cycle CSV export? Held by
 * every platform-access role except the read-only ones (Executive).
 */
export const EXPORT_CYCLE = 'cycle:export';

/* --- Row-level keys (f9.2) ----------------------------------------------------
   Projected per row from f9.1's grant table by the same policy function the
   write routes call, so a row never advertises a control its route refuses. */

/** Edit, deactivate or reactivate THIS node. */
export const EDIT_NODE = 'hierarchy:edit';
/** Create a child beneath THIS node. */
export const CREATE_CHILD = 'hierarchy:create_child';
/** Assign or end a delivery-role holder on THIS node. */
export const ASSIGN_OWNER = 'hierarchy:assign_owner';
/** f15.1: create an artifact on THIS node, or upload a version to one of its artifacts. */
export const PUBLISH_ARTIFACT = 'artifact:publish';

/** Whether the server said the caller may do `key` to this row. */
export function rowCan(row: { permissions?: readonly string[] }, key: string): boolean {
  return (row.permissions ?? []).includes(key);
}
